"use client";

import { XIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { cn } from "../../lib/utils";

interface ImageZoomProps {
  children: React.ReactNode;
  src: string;
  alt?: string;
  className?: string;
}

export function ImageZoom({ children, src, alt = "", className }: ImageZoomProps) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;

    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = original;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, close]);

  return (
    <>
      <div
        className={cn("cursor-zoom-in", className)}
        onClick={open}
        onKeyDown={e => e.key === "Enter" && open()}
      >
        {children}
      </div>

      {isOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
            onClick={close}
            onKeyDown={e => e.key === "Escape" && close()}
          >
            <button
              type="button"
              onClick={close}
              className="absolute right-4 top-4 z-10 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
              aria-label="닫기"
            >
              <XIcon className="size-6" />
            </button>

            <TransformWrapper
              initialScale={1}
              minScale={0.5}
              maxScale={5}
              doubleClick={{ mode: "toggle", step: 2 }}
              centerOnInit
            >
              <TransformComponent
                wrapperStyle={{ width: "100%", height: "100%" }}
                contentStyle={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={src}
                  alt={alt}
                  className="max-h-screen max-w-full object-contain"
                  draggable={false}
                />
              </TransformComponent>
            </TransformWrapper>
          </div>,
          document.body,
        )}
    </>
  );
}
